import styled from "styled-components";
import { FC, PropsWithChildren } from "react";

import Text from "@/components/Text";
import { hexToRgba } from "@/utils/theme";

const NoteBox = styled.div`
  background: ${({ theme }) => hexToRgba(theme.colors.grayscale, 0.5)};
  border-radius: 4px;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
`;

const NoteHeading = styled.div`
  display: flex;
  margin-bottom: 8px;
  align-items: center;
  svg {
    margin-right: 6px;
  }
`;

const StyledText = styled(Text)`
  word-break: break-word;
  a {
    text-decoration: underline;
  }
  ul {
    list-style-type: disc;
    list-style-position: inside;
    margin-left: 6px;
  }
`;

interface Props {
  title?: string;
  type?: "question";
  className?: string;
  textContent?: string;
}

const Note: FC<PropsWithChildren<Props>> = ({
  children,
  title = "Note",
  type = "exclamation",
  className,
  textContent,
}) => {
  const urlify = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.replace(urlRegex, function (url: string) {
      return '<a href="' + url + '">' + url + "</a>";
    });
  };

  return (
    <NoteBox className={className}>
      <NoteHeading>
        {type === "exclamation" && (
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="8" cy="8" r="7.5" stroke="#E9E9E9" />
            <path
              d="M7.25 5.73529V4.5H8.75V5.73529H7.25ZM7.25 11.5V6.55882H8.75V11.5H7.25Z"
              fill="#222222"
            />
          </svg>
        )}
        {type === "question" && (
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="8" cy="8" r="7.5" stroke="#E9E9E9" />
            <path
              d="M7.22853 9.69278C7.22853 9.48083 7.25283 9.26888 7.30142 9.05693C7.35326 8.84498 7.42939 8.6523 7.52983 8.47888C7.61082 8.3151 7.69829 8.18665 7.79225 8.09352C7.88944 7.99718 7.99959 7.89923 8.1227 7.79968C8.24905 7.70013 8.39808 7.56043 8.56978 7.3806C8.70261 7.23608 8.79009 7.09478 8.8322 6.9567C8.87756 6.8154 8.90024 6.66125 8.90024 6.49426C8.90024 6.40755 8.89052 6.32406 8.87108 6.24377C8.85488 6.16349 8.82248 6.09284 8.77389 6.03182C8.69613 5.92585 8.59246 5.85038 8.46287 5.80542C8.33652 5.75725 8.20045 5.73317 8.05466 5.73317C7.94451 5.73317 7.83598 5.74601 7.72907 5.7717C7.6254 5.79739 7.53631 5.83914 7.46179 5.89694C7.37108 5.96438 7.30304 6.04948 7.25769 6.15225C7.21557 6.25501 7.19613 6.36902 7.19937 6.49426H5.7998C5.83868 6.15064 5.93749 5.83593 6.09624 5.55012C6.25499 5.26431 6.46071 5.03951 6.71341 4.87573C6.89808 4.7537 7.10218 4.66057 7.32572 4.59634C7.54926 4.53211 7.77281 4.5 7.99635 4.5C8.36568 4.5 8.71395 4.5578 9.04117 4.67341C9.36838 4.78581 9.6389 4.97207 9.85272 5.23219C9.99527 5.39597 10.1054 5.58705 10.1832 5.80542C10.2609 6.02058 10.2998 6.23735 10.2998 6.45572C10.2998 6.80576 10.2204 7.12369 10.0617 7.4095C9.90294 7.69531 9.70693 7.96185 9.47367 8.20913C9.37324 8.3151 9.28253 8.40823 9.20153 8.48852C9.12378 8.56559 9.0525 8.63945 8.98771 8.7101C8.92615 8.77754 8.87108 8.84819 8.82248 8.92205C8.73177 9.06977 8.67669 9.1902 8.65726 9.28333C8.63782 9.37646 8.6281 9.51294 8.6281 9.69278H7.22853ZM7.23825 11.5714V10.1841H8.6281V11.5714H7.23825Z"
              fill="#222222"
            />
          </svg>
        )}
        <Text as="span" $type="smallBold" $color="black">
          {title}
        </Text>
      </NoteHeading>
      {!!textContent ? (
        <StyledText
          dangerouslySetInnerHTML={{ __html: urlify(textContent) }}
          $type="smallMedium"
          $color="gray"
        />
      ) : (
        <StyledText $type="smallMedium" $color="gray">
          {children}
        </StyledText>
      )}
    </NoteBox>
  );
};

export default Note;
